import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/apiService';

const Profile = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phoneNumber: user?.phoneNumber || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await userService.updateProfile(formData);
      updateUser(data.user);
      toast.success('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-6 sm:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">My Profile</h1>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-700 p-5 sm:p-8 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="w-16 h-16 sm:w-24 sm:h-24 bg-white rounded-2xl flex items-center justify-center text-3xl sm:text-4xl">
                👤
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold break-words">{user?.name}</h2>
                <p className="opacity-90 mt-1 text-sm sm:text-base break-all">{user?.collegeEmail}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-3 py-1 bg-white/15 rounded-full text-sm font-medium">
                    {user?.role?.replace('_', ' ')}
                  </span>
                  {user?.isEmailVerified && (
                    <span className="px-3 py-1 bg-emerald-500 rounded-full text-sm font-medium">
                      ✓ Email Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="p-5 sm:p-8">
            {editing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      pattern="[6-9][0-9]{9}"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
                    <p className="text-base sm:text-lg text-slate-900 break-words">{user?.name}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">College Email</label>
                    <p className="text-base sm:text-lg text-slate-900 break-all">{user?.collegeEmail}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Phone Number</label>
                    <p className="text-base sm:text-lg text-slate-900 break-words">{user?.phoneNumber}</p>
                  </div>

                  {/* Role - Commented out for now */}
                  {/* <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Role</label>
                    <p className="text-lg text-slate-900">{user?.role?.replace('_', ' ')}</p>
                  </div> */}

                  {user?.role === 'STUDENT' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">USN</label>
                        <p className="text-base sm:text-lg text-slate-900 break-words">{user?.usn}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Department</label>
                        <p className="text-base sm:text-lg text-slate-900 break-words">{user?.department}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Semester</label>
                        <p className="text-base sm:text-lg text-slate-900 break-words">{user?.semester?.replace('SEM_', 'Semester ')}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Section</label>
                        <p className="text-base sm:text-lg text-slate-900 break-words">{user?.section || 'N/A'}</p>
                      </div>
                    </>
                  )}

                  {/* Account Status - Commented out for now */}
                  {/* <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Account Status</label>
                    <p className="text-lg">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        user?.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div> */}
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  <button
                    onClick={handleLogout}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                  >
                    Logout
                  </button>
                </div>

                {user?.role === 'STUDENT' && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    If any data here is not right, contact your HOD ASAP.
                  </div>
                )}

                {/* Edit Profile button - Commented out */}
                {/* <button
                  onClick={() => setEditing(true)}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
                >
                  Edit Profile
                </button> */}
              </div>
            )}
          </div>
        </div>

        {/* Verification Status - Commented out for now */}
        {/* <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Verification Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-slate-700">Email Verification</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                user?.isEmailVerified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
              }`}>
                {user?.isEmailVerified ? '✓ Verified' : '⏳ Pending'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-slate-700">Phone Verification</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                user?.isPhoneVerified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
              }`}>
                {user?.isPhoneVerified ? '✓ Verified' : '⏳ Pending'}
              </span>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Profile;
